import usaStates from '../data/States';
import type {
  AddressChange,
  AddressIssue,
  AddressValidationItem,
  AddressValidationRequest,
  AddressValidationResponse,
  AddressValidationResult,
} from '../types/addressValidation';

import { apiClient } from './api-client';
import { getCurrentSession, refreshSession } from './auth';
import { getHeaders } from './utils';

const ADDRESS_VALIDATE_PATH = '/address/validate';

/**
 * Resolve the current Cognito access token.
 *
 * @remarks
 * The backend `/address/validate` endpoint requires a Cognito JWT with a read scope,
 * fetched internally so utility-layer callers need not thread a token through.
 *
 * The token is spent immediately, so the expiry check is required: without it an
 * expired token reaches the API and every test still passes. `refreshSession()` is the
 * fallback, not the default — this runs on address-field blur, so refreshing
 * unconditionally would put a token round trip in front of each one.
 */
const getAccessToken = async (): Promise<string> => {
  const session = await getCurrentSession();
  if (session && session.expiresAtMs > Date.now()) {
    return session.accessToken;
  }
  return (await refreshSession()).accessToken;
};

/**
 * Validate one or more postal addresses against the backend proxy.
 *
 * @param addresses - Up to 25 address items to validate in a single request.
 * @param options - Optional request-level flags (e.g., `includeRaw`).
 * @param token - Optional pre-fetched access token. When omitted, the token
 * is resolved from the current OIDC session.
 * @returns The full batch response, including per-address verdicts.
 */
export const validateAddresses = async (
  addresses: AddressValidationItem[],
  options?: AddressValidationRequest['options'],
  token?: string
): Promise<AddressValidationResponse> => {
  const authToken = token ?? (await getAccessToken());
  const body: AddressValidationRequest = { addresses, ...(options ? { options } : {}) };

  return await apiClient.post<AddressValidationResponse>(import.meta.env.VITE_API_NAME, ADDRESS_VALIDATE_PATH, {
    headers: getHeaders(authToken),
    body,
  });
};

/**
 * Resolve a state's 2-letter USPS abbreviation from either the full name
 * or the abbreviation itself.
 *
 * @remarks
 * The backend expects `administrativeArea` as a 2-letter code; forms in this
 * app sometimes carry the full state name.
 */
export const resolveStateAbbrev = (state: string): string => {
  if (!state) return state;
  const match = usaStates.list.find((s) => s.value === state || s.key === state);
  return match?.key || state;
};

/** Reverse lookup: 2-letter code → full state name, falling back to the code. */
export const resolveStateName = (abbrev: string): string => {
  const match = usaStates.list.find((s) => s.key === abbrev);
  return match?.value || abbrev;
};

/** User-provided input needed to produce per-component diff messages. */
export interface AddressValidationInputSummary {
  addressLine1?: string | undefined;
  addressLine2?: string | undefined;
  city?: string | undefined;
  /** 2-letter abbreviation, as sent to the backend. */
  stateAbbrev?: string | undefined;
  zip?: string | undefined;
}

/**
 * Format a single backend-emitted {@link AddressChange} into the UI's error
 * phrasing. Returns `undefined` when the change has no actionable `to` value
 * (e.g., a removed component) so the caller can skip it.
 */
const formatChangeMessage = (change: AddressChange): string | undefined => {
  if (!change?.to) return undefined;

  switch (change.field) {
    case 'postalCode':
      return 'Invalid Zip Code, Suggested Zip Code is: ' + change.to;
    case 'city':
      return 'Invalid City, Suggested City name is: ' + change.to;
    case 'region':
      return 'Invalid State, Suggested State is: ' + resolveStateName(change.to);
    case 'street':
      return 'Invalid Address, Suggested Address is: ' + change.to;
    case 'country':
      return 'Invalid Country, Suggested Country is: ' + change.to;
    default:
      return undefined;
  }
};

/**
 * Legacy positional diff against `suggestion` / `normalized`. Used only as a fallback
 * when the backend response is missing `changes` (pre-deploy environments); new
 * responses drive UI copy entirely from `changes[]`.
 *
 * @remarks
 * False-positives when `line1` / `line2` are reformatted (e.g. apt-78 merged into
 * line1).
 */
const legacySuggestionDiff = (
  result: AddressValidationResult,
  input: AddressValidationInputSummary
): string[] => {
  const suggestion = result.suggestion ?? result.normalized;
  const out: string[] = [];
  if (!suggestion) return out;

  const suggestedZip5 = suggestion.postalCode?.split('-')[0];
  const inputZip5 = input.zip?.split('-')[0];
  if (suggestedZip5 && suggestedZip5 !== inputZip5) {
    out.push('Invalid Zip Code, Suggested Zip Code is: ' + suggestedZip5);
  }

  if (suggestion.city && suggestion.city.toUpperCase() !== input.city?.toUpperCase()) {
    out.push('Invalid City, Suggested City name is: ' + suggestion.city);
  }

  if (suggestion.region && suggestion.region !== input.stateAbbrev) {
    out.push('Invalid State, Suggested State is: ' + resolveStateName(suggestion.region));
  }

  if (suggestion.line1 && suggestion.line1.toUpperCase() !== input.addressLine1?.toUpperCase()) {
    out.push('Invalid Address, Suggested Address is: ' + suggestion.line1);
  }

  if (
    suggestion.line2 &&
    (input.addressLine2 ?? '').toUpperCase() !== suggestion.line2.toUpperCase()
  ) {
    out.push('Invalid Address Line 2, Suggested is: ' + suggestion.line2);
  }

  return out;
};

/**
 * Convert a single {@link AddressValidationResult} into a flat list of
 * user-facing error messages.
 *
 * @remarks
 * Primary signal is `result.changes[]` (always present on new backend
 * responses, may be `undefined` on legacy ones). The backend already does
 * the canonical line1+line2 merge so apt/suite reformatting can't produce a
 * spurious "Invalid Address" message.
 *
 * Verdict-specific behavior:
 * - `CONFIRMED` → return `[]`.
 * - `CORRECTED` → return change-driven messages; if `changes` is absent
 *   (legacy response), fall back to positional comparison.
 * - `UNCONFIRMED` → return change-driven messages; additionally surface
 *   `MISSING:subpremise|premise` issue (apt/suite needed). If both empty,
 *   emit a generic "Address Not Found".
 * - `REJECTED` → return change-driven messages (rare); else map
 *   `issues[0].type` to a specific copy.
 */
export const buildAddressValidationErrors = (
  result: AddressValidationResult,
  input: AddressValidationInputSummary
): string[] => {
  if (result.verdict === 'CONFIRMED') return [];

  const issues: AddressIssue[] = result.issues ?? [];
  const hasChangesField = Array.isArray(result.changes);
  const messages: string[] = [];

  // Primary path: iterate `changes[]` regardless of verdict.
  for (const change of result.changes ?? []) {
    const msg = formatChangeMessage(change);
    if (msg) messages.push(msg);
  }

  switch (result.verdict) {
    case 'CORRECTED': {
      if (!messages.length) {
        if (!hasChangesField) {
          // Legacy backend response — recover via positional compare.
          messages.push(...legacySuggestionDiff(result, input));
        }
        if (!messages.length) {
          messages.push('Address was corrected by USPS. Please verify and update the fields.');
        }
      }
      break;
    }

    case 'UNCONFIRMED': {
      const missingSubpremise = issues.some(
        (i) => i.type === 'MISSING' && (i.component === 'subpremise' || i.component === 'premise')
      );
      if (missingSubpremise) {
        messages.push(
          'The address was found but more information is needed (such as an apartment, suite, or box number) to match to a specific address.'
        );
      } else if (!messages.length) {
        if (issues.length) messages.push(...issues.map((i) => i.message));
        else messages.push('Address Not Found. Please verify the street address is correct.');
      }
      break;
    }

    case 'REJECTED':
    default: {
      if (!messages.length) {
        const primary = issues[0];
        switch (primary?.type) {
          case 'PO_BOX_NOT_ALLOWED':
            messages.push('PO Boxes are not allowed for delivery addresses.');
            break;
          case 'UNSUPPORTED_REGION':
            messages.push('Only US addresses are supported.');
            break;
          case 'UNDELIVERABLE':
            messages.push('Address is undeliverable per USPS.');
            break;
          case 'INVALID_INPUT':
            messages.push(primary.message || 'Invalid address input.');
            break;
          case 'UPSTREAM_ERROR':
            messages.push('Unable to validate address.');
            break;
          default:
            messages.push(result.summary || primary?.message || 'Unable to validate address.');
        }
      }
      break;
    }
  }

  // Dedupe while preserving order — guards against rare duplicates between
  // change- and issue-driven paths.
  return Array.from(new Set(messages));
};
